/**
 * Network status indicator that shows connection issues
 */
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { WifiOff, Wifi } from 'lucide-react';
import { useEffect, useState } from 'react';

export const NetworkStatusIndicator = () => {
  const { isOnline, isSlowConnection, effectiveType, rtt } = useNetworkStatus();
  const [showIndicator, setShowIndicator] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShowIndicator(true);
      setWasOffline(true);
    } else if (wasOffline) {
      // Show "back online" message briefly
      setShowIndicator(true);
      const timer = setTimeout(() => {
        setShowIndicator(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    } else if (isSlowConnection) {
      setShowIndicator(true);
    } else {
      setShowIndicator(false);
    }
  }, [isOnline, isSlowConnection, wasOffline]);

  if (!showIndicator) return null;

  return (
    <div
      className={`fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-auto z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${
        !isOnline
          ? 'bg-red-500 text-white'
          : wasOffline
          ? 'bg-green-500 text-white'
          : 'bg-yellow-500 text-white'
      }`}
    >
      {!isOnline ? (
        <WifiOff className="h-5 w-5 shrink-0" />
      ) : (
        <Wifi className="h-5 w-5 shrink-0" />
      )}
      
      <div className="text-sm font-medium">
        {!isOnline ? (
          <>
            <div className="font-semibold">You're offline</div>
            <div className="text-xs opacity-90">Trying to reconnect...</div>
          </>
        ) : wasOffline ? (
          <>
            <div className="font-semibold">Back online!</div>
            <div className="text-xs opacity-90">Connection restored</div>
          </>
        ) : (
          <>
            <div className="font-semibold">Slow connection detected</div>
            <div className="text-xs opacity-90">
              {effectiveType !== 'unknown' && `${effectiveType.toUpperCase()}`}
              {rtt !== null && ` • ${rtt}ms latency`}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

